/**
 * Floors of a house (web wizard, web and mobile "Etagen bearbeiten", server checks).
 *
 * A floor is stored as `{ id, label, order }`; higher `order` is higher up. The assistants work with short
 * codes: `DG` (attic), `OG<n>` (upper floors), `EG` (ground floor), `KG` (cellar, the first level below
 * ground), `UG<n>` (further basements, n ≥ 2 next to a KG) and `P` (parking, only in the web wizard, never
 * stored as a floor). Every floor can hold any kind of unit: flats in the attic or the basement are fine.
 */
export type FloorRow = { id?: string; label: string; order: number };
/** Where a new floor goes: one more upper floor (or the ground floor), the attic, one more level below ground. */
export type FloorSlot = 'above' | 'roof' | 'below';

export const ROOF_ORDER = 100;

/** Sort order of a code (DG 100, OG<n> n, EG 0, KG -1, UG<n> -n, P -100). */
export function floorRank(code: string): number {
  if (code === 'DG') return ROOF_ORDER;
  if (code === 'EG') return 0;
  if (code === 'KG') return -1;
  if (code === 'P') return -100;
  const n = Number(code.slice(2)) || 1;
  return code.startsWith('OG') ? n : -n;
}

/** Label stored on the property: "OG2" → "2. OG", "UG2" → "2. UG", others unchanged. */
export function floorLabel(code: string): string {
  if (/^(OG|UG)\d+$/.test(code)) return `${code.slice(2)}. ${code.slice(0, 2)}`;
  return code;
}

/** Code of a stored label ("2. OG" → "OG2", "EG" → "EG"); null for free labels ("Souterrain"). */
export function floorCode(label: string): string | null {
  const l = label.trim().toUpperCase();
  const pre = /^(\d+)\.?\s*(OG|UG)$/.exec(l); // "2. OG"
  if (pre) return `${pre[2]}${Number(pre[1])}`;
  const post = /^(OG|UG)\s*(\d+)$/.exec(l); // "OG2"
  if (post) return `${post[1]}${Number(post[2])}`;
  if (l === 'OG' || l === 'UG') return `${l}1`;
  const one = /^(DG|EG|KG)\s*\.?\s*1$/.exec(l); // "EG1": the one ground floor, as some imports write it
  if (one) return one[1]!;
  return ['DG', 'EG', 'KG'].includes(l) ? l : null;
}

/** Translation lookup of a label: `{ key: 'OGn', n: 2 }` for "2. OG", `{ key: 'EG' }`, null for free labels. */
export function floorNameKey(label: string): { key: 'DG' | 'EG' | 'KG' | 'OGn' | 'UGn'; n?: number } | null {
  const c = floorCode(label);
  if (!c) return null;
  if (c === 'DG' || c === 'EG' || c === 'KG') return { key: c };
  return { key: c.startsWith('OG') ? 'OGn' : 'UGn', n: Number(c.slice(2)) };
}

/**
 * Whether a floor lies below ground (cellar "KG", basements "UG", "2. UG" …); the house drawings shade
 * those rows and draw the ground line above the first one. Free labels ("Souterrain") fall back to the
 * order: below the ground floor (order < 0) counts as below ground.
 */
export function isBelowGround(f: { label: string; order?: number }): boolean {
  const c = floorCode(f.label);
  if (c) return c === 'KG' || c.startsWith('UG');
  return f.order != null && f.order < 0;
}

/** Top floor first (same order as the house drawing). */
export const sortFloorsDesc = <T extends { order: number }>(l: readonly T[]): T[] => [...l].sort((a, b) => b.order - a.order);

/** Code of the next floor for a slot, or null (there is already an attic). `codes` are the current floor codes. */
export function nextFloorCode(codes: readonly string[], slot: FloorSlot): string | null {
  const has = (c: string) => codes.includes(c);
  const num = (p: string) => codes.filter((c) => c.startsWith(p)).map((c) => Number(c.slice(2)) || 1);
  if (slot === 'roof') return has('DG') ? null : 'DG';
  const og = num('OG');
  if (slot === 'above') return has('EG') || og.length ? `OG${Math.max(0, ...og) + 1}` : 'EG';
  // only upper floors so far (a flat on the 2nd floor): the ground floor is the next one down
  if (!has('EG') && og.length) return 'EG';
  // a plain "UG" counts as the first level below ground, like the cellar
  return has('KG') || has('UG1') ? `UG${Math.max(1, ...num('UG')) + 1}` : 'KG';
}

/**
 * The floor a slot adds to existing floors (free labels included), or null. The order keeps the house
 * consistent even with hand-made orders: upper floors stay below the attic, basements below everything.
 */
export function nextFloor(floors: readonly FloorRow[], slot: FloorSlot): { code: string; label: string; order: number } | null {
  const code = nextFloorCode(floors.map((f) => floorCode(f.label)).filter((c): c is string => !!c), slot);
  if (!code) return null;
  const roof = floors.find((f) => floorCode(f.label) === 'DG');
  const rest = floors.filter((f) => f !== roof).map((f) => f.order);
  let order = floorRank(code);
  if (slot === 'roof') order = Math.max(order, ...floors.map((f) => f.order + 1));
  else if (slot === 'above') order = Math.max(order, ...rest.map((o) => o + 1));
  else order = Math.min(order, ...rest.map((o) => o - 1));
  return { code, label: floorLabel(code), order };
}

/** Stored floors for a list of codes (top first); `P` (parking) is not a floor and is left out. */
export const floorsFromCodes = (codes: readonly string[]): FloorRow[] =>
  sortFloorsDesc(codes.filter((c) => c !== 'P').map((c) => ({ label: floorLabel(c), order: floorRank(c) })));

/**
 * Floors with one more floor in a slot (top first); unchanged when the slot is not available. Orders are
 * integers, so when an attic sits right above the top floor (hand-made "DG" order 3 over "2. OG" order 2)
 * the attic moves up one step to stay on top (its id stays).
 */
export function addFloor<T extends FloorRow>(floors: readonly T[], slot: FloorSlot): (T | FloorRow)[] {
  const f = nextFloor(floors, slot);
  if (!f) return sortFloorsDesc(floors);
  const list: (T | FloorRow)[] = floors.map((x) => (slot === 'above' && floorCode(x.label) === 'DG' && x.order <= f.order ? { ...x, order: f.order + 1 } : x));
  return sortFloorsDesc([...list, { label: f.label, order: f.order }]);
}

/** Ids of floors that `next` drops although units still sit on them (the server refuses those). */
export function blockedFloorRemovals(current: readonly { id: string }[], next: readonly FloorRow[], usedFloorIds: Iterable<string | null | undefined>): string[] {
  const keep = new Set(next.map((f) => f.id).filter(Boolean));
  const used = new Set([...usedFloorIds].filter(Boolean));
  return current.filter((f) => !keep.has(f.id) && used.has(f.id)).map((f) => f.id);
}

/**
 * Rank of a free-text position inside a floor, left to right like the house drawing: "links" 0, "Mitte" 1,
 * "rechts" 2 (also "left/middle/center/right", "li."/"re."); anything else and no position come after them (3).
 */
export function positionRank(position: string | null | undefined): number {
  const p = (position ?? '').trim().toLowerCase();
  if (!p) return 3;
  const word = (w: string) => new RegExp(`(^|[^a-zäöüß])(${w})(?![a-zäöüß])`).test(p);
  if (word('links|left|li')) return 0;
  if (word('mitte|mittig|middle|center|centre')) return 1;
  if (word('rechts|right|re')) return 2;
  return 3;
}

type OrderedUnit = { sortIndex?: number | null; label: string; position?: string | null };

/**
 * Order of the units inside a floor (house drawing left to right, lists top to bottom): `sortIndex` first
 * (set by POST /properties/:id/units/order), units without one after them, then by position (links, Mitte,
 * rechts, others), then by label (natural: "WE 2" < "WE 10").
 */
export function compareUnitOrder(a: OrderedUnit, b: OrderedUnit): number {
  const ai = a.sortIndex ?? Number.POSITIVE_INFINITY;
  const bi = b.sortIndex ?? Number.POSITIVE_INFINITY;
  if (ai !== bi) return ai < bi ? -1 : 1;
  const ap = positionRank(a.position);
  const bp = positionRank(b.position);
  if (ap !== bp) return ap - bp;
  return a.label.localeCompare(b.label, 'de', { numeric: true });
}

/**
 * Where a floor sits in the house drawing, as a sort key (lower = further up): above-ground floors top down
 * (DG, OG …, EG), then units without a (known) floor, then cellar and basements top down (KG, 2. UG …).
 */
export function floorSortKey(floors: readonly { id: string; label: string; order: number }[], floorId: string | null | undefined): number {
  const f = floorId ? floors.find((x) => x.id === floorId) : undefined;
  if (!f) return 0;
  // orders are small integers (DG 100 … UG -n); keep the three bands apart
  return isBelowGround(f) ? 1_000_000 - f.order : -1_000_000 - f.order;
}

/**
 * The one unit order of every list (overview table, Einheiten list, object cards, unit pickers, timeline lanes):
 * the same as the house drawing, floors top to bottom, units without a floor after the above-ground floors,
 * inside a floor `compareUnitOrder`.
 */
export function compareHouseOrder(floors: readonly { id: string; label: string; order: number }[]) {
  return (a: OrderedUnit & { floorId?: string | null }, b: OrderedUnit & { floorId?: string | null }): number =>
    floorSortKey(floors, a.floorId) - floorSortKey(floors, b.floorId) || compareUnitOrder(a, b);
}

/** A copy of `units` in house order (see compareHouseOrder). */
export const sortUnitsLikeHouse = <T extends OrderedUnit & { floorId?: string | null }>(units: readonly T[], floors: readonly { id: string; label: string; order: number }[]): T[] =>
  [...units].sort(compareHouseOrder(floors));
